import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2, Sparkles, Shield, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { markSlideViewed } from '../utils/progressTracker';

const ACTIVITY_ID = 'caferuis-bc3';

const theme = {
  primary: '#DB2777',
  secondary: '#6366F1',
  bg: '#FDF2F8',
  text: '#1a2e35',
};

const bgImage = "https://static.prod-images.emergentagent.com/jobs/1961affb-6e82-4a8a-b43a-1af358e9a95a/images/272016a05e8b35efaad949ec4be021e8219e4804b533156f07cc760d5b1ad156.png";

const slides = [
  // =============================
  // INTRODUCTION DU BLOC 3
  // =============================

  // SLIDE 0 — TITRE
  { type: 'title', title: "Bloc 3", subtitle: "Gérer les volets administratif, logistique et budgétaire d'une unité d'intervention sociale",
    narration: "Bienvenue dans le Bloc 3 du CAFERUIS : Gérer les volets administratif, logistique et budgétaire. Ce bloc couvre la gestion administrative, la logistique, l'élaboration et le suivi du budget, et la communication financière." },

  // SLIDE 1 — Présentation du bloc
  { type: 'content', title: "Présentation du Bloc 3",
    intro: "Le Bloc 3 du CAFERUIS porte sur la gestion des volets administratif, logistique et budgétaire dans le secteur social et médico-social.",
    points: [
      "Enjeux de la gestion administrative et financière dans le secteur social et médico-social",
      "Le rôle du responsable d'unité dans la gestion des ressources",
      "Articulation entre gestion administrative, logistique et pilotage budgétaire"
    ],
    highlight: "Le responsable d'unité doit maîtriser les dimensions administratives, logistiques et budgétaires pour garantir le bon fonctionnement du service et la qualité de l'accompagnement.",
    narration: "Le Bloc 3 aborde les enjeux de la gestion administrative et financière, le rôle du responsable d'unité et l'articulation entre administration, logistique et budget." },

  // SLIDE 2 — SOMMAIRE GÉNÉRAL
  { type: 'sommaire', title: "Sommaire du Bloc 3",
    sections: [
      { num: "1", title: "Chapitre 1 — Assurer la gestion administrative d'une UIS", targetSlide: 5 },
      { num: "2", title: "Chapitre 2 — Organiser la logistique d'une UIS", targetSlide: 35 },
      { num: "3", title: "Chapitre 3 — Élaborer et suivre le budget", targetSlide: 65 },
      { num: "4", title: "Chapitre 4 — Gérer l'exécution et le suivi du budget", targetSlide: 89 },
      { num: "5", title: "Chapitre 5 — Communiquer dans le cadre de la gestion administrative et budgétaire", targetSlide: 113 }
    ],
    narration: "Le Bloc 3 couvre 5 chapitres : gestion administrative, logistique, élaboration budgétaire, suivi du budget et communication financière." },

  // SLIDE 3 — Articulation
  { type: 'content', title: "Articulation administration, logistique et budget",
    intro: "La gestion d'une unité d'intervention sociale repose sur trois piliers complémentaires :",
    points: [
      "La gestion administrative : traçabilité, conformité réglementaire, protection des données",
      "La gestion logistique : locaux, équipements, approvisionnements, développement durable",
      "Le pilotage budgétaire : élaboration du budget, suivi des dépenses, communication financière"
    ],
    highlight: "Ces trois dimensions sont interdépendantes : une bonne gestion administrative sécurise les décisions budgétaires, et une logistique efficace optimise l'utilisation des ressources.",
    narration: "Les trois piliers — administration, logistique, budget — sont interdépendants et contribuent à la qualité de l'accompagnement des usagers." },

  // SLIDE 4 — Enjeux spécifiques
  { type: 'content', title: "Les enjeux spécifiques du secteur social",
    intro: "La gestion administrative et financière dans le secteur social présente des spécificités importantes :",
    points: [
      "Financements publics encadrés par les autorités de tarification (ARS, conseils départementaux)",
      "Obligations de transparence et de reddition de comptes renforcées",
      "Protection des données sensibles des usagers (RGPD)",
      "Contraintes réglementaires spécifiques (ERP, conventions collectives)",
      "Articulation entre efficience économique et qualité de l'accompagnement"
    ],
    narration: "Le secteur social a des spécificités : financements publics, transparence, protection des données, contraintes réglementaires et articulation économie-qualité." },

  // =============================
  // CHAPITRE 1 — Gestion administrative
  // =============================

  // SLIDE 5 — Transition
  { type: 'section', title: "Chapitre 1", subtitle: "Assurer la gestion administrative d'une unité d'intervention sociale",
    narration: "Chapitre 1 : Assurer la gestion administrative d'une unité d'intervention sociale." },

  // SLIDE 6 — Intro Chap 1
  { type: 'content', title: "Introduction — Chapitre 1",
    intro: "La gestion administrative constitue une dimension essentielle du fonctionnement d'une unité d'intervention sociale. Elle permet d'assurer la traçabilité des actions menées, la conformité aux obligations réglementaires et la bonne circulation de l'information.",
    intro2: "Le responsable d'unité doit veiller à structurer et organiser les processus administratifs afin de garantir :",
    points: [ "La fiabilité des informations", "La continuité du service", "Le respect du cadre réglementaire", "La protection des données personnelles" ],
    highlight: "La gestion administrative ne se limite pas à une fonction de support. Elle contribue directement à la qualité de l'accompagnement des usagers et à la sécurisation des pratiques professionnelles.",
    narration: "La gestion administrative assure traçabilité, conformité et circulation de l'information. Elle contribue directement à la qualité de l'accompagnement." },

  // SLIDE 7 — Objectifs
  { type: 'content', title: "Objectifs pédagogiques — Chapitre 1",
    intro: "À l'issue de ce chapitre, le professionnel doit être capable de :",
    points: [ "Organiser le fonctionnement administratif d'une unité d'intervention sociale", "Structurer les circuits de traitement de l'information", "Garantir la gestion et la conservation des documents administratifs", "Utiliser les systèmes d'information professionnels", "Assurer la protection des données personnelles des usagers et des professionnels" ],
    narration: "Objectifs : organiser l'administration, structurer les circuits d'information, gérer les documents, utiliser les SI et protéger les données." },

  // SLIDE 8 — Sommaire Chap 1
  { type: 'grid', title: "Chapitre 1 — Sommaire détaillé",
    items: [
      { num: "1", label: "L'organisation administrative d'une unité" },
      { num: "2", label: "Les circuits de validation et la traçabilité" },
      { num: "3", label: "Le rôle du secrétariat et la coordination" },
      { num: "4", label: "La gestion du courrier et des correspondances" },
      { num: "5", label: "Les principes de la gestion documentaire" },
      { num: "6", label: "L'archivage physique et numérique" },
      { num: "7", label: "Les durées légales de conservation" },
      { num: "8", label: "Les systèmes d'information (SI)" },
      { num: "9", label: "Le dossier usager informatisé (DUI)" },
      { num: "10", label: "La sécurité des données" },
      { num: "11", label: "Le RGPD" },
      { num: "12", label: "Les droits des usagers sur leurs données" }
    ],
    narration: "Le Chapitre 1 couvre 12 thèmes : organisation administrative, circuits de validation, secrétariat, courrier, gestion documentaire, archivage, conservation, SI, DUI, sécurité, RGPD et droits des usagers." },

  // ===== S1 — Organisation administrative =====
  { type: 'section', title: "1. L'organisation administrative d'une unité", narration: "L'organisation administrative d'une unité." },

  // SLIDE 10
  { type: 'content', title: "Définition de l'organisation administrative",
    intro: "L'organisation administrative correspond à l'ensemble des procédures et des outils permettant d'assurer la gestion et la circulation des informations au sein d'une organisation.",
    intro2: "Elle comprend notamment :",
    points: [ "La gestion des documents administratifs", "La coordination des activités de secrétariat", "La structuration des circuits de validation", "La gestion du courrier et des communications officielles" ],
    highlight: "Dans une unité d'intervention sociale, l'organisation administrative contribue à garantir la cohérence entre les actions menées par les professionnels et les obligations institutionnelles.",
    narration: "L'organisation administrative gère documents, secrétariat, circuits de validation et courrier. Elle assure la cohérence entre actions et obligations." },

  // ===== S2 — Circuits de validation =====
  { type: 'section', title: "2. Les circuits de validation et la traçabilité des décisions", narration: "Les circuits de validation et la traçabilité des décisions." },

  // SLIDE 12
  { type: 'content', title: "Les circuits de validation",
    intro: "Les circuits de validation correspondent aux différentes étapes par lesquelles une information ou un document doit passer avant d'être validé.",
    intro2: "Ces circuits permettent de :",
    points: [ "Sécuriser les décisions", "Garantir la conformité des documents", "Assurer la traçabilité des actions" ],
    intro3: "Par exemple, un projet personnalisé peut nécessiter :",
    points2: [ "Élaboration par l'équipe pluridisciplinaire", "Validation par le responsable d'unité", "Signature par la direction" ],
    highlight: "Le responsable d'unité doit veiller à ce que ces circuits soient clairement définis et connus des professionnels.",
    narration: "Les circuits de validation sécurisent les décisions et garantissent la traçabilité. Un projet personnalisé passe par l'équipe, le responsable puis la direction." },

  // ===== S3 — Secrétariat =====
  { type: 'section', title: "3. Le rôle du secrétariat et la coordination administrative", narration: "Le rôle du secrétariat et la coordination administrative." },

  // SLIDE 14
  { type: 'content', title: "Le rôle du secrétariat",
    intro: "Le secrétariat joue un rôle central dans l'organisation administrative d'une unité.",
    intro2: "Ses missions peuvent inclure :",
    points: [ "L'accueil téléphonique et physique", "La gestion des agendas", "La rédaction et la diffusion des documents administratifs", "La gestion des dossiers des usagers" ],
    highlight: "Le responsable d'unité doit coordonner les activités administratives afin d'assurer la fluidité des échanges d'informations.",
    narration: "Le secrétariat assure accueil, agendas, documents et dossiers usagers. Le responsable coordonne ces activités pour fluidifier l'information." },

  // ===== S4 — Courrier =====
  { type: 'section', title: "4. La gestion du courrier et des correspondances", narration: "La gestion du courrier et des correspondances institutionnelles." },

  // SLIDE 16
  { type: 'content', title: "La gestion du courrier",
    intro: "La gestion du courrier constitue une activité administrative importante.",
    intro2: "Elle comprend :",
    points: [ "La réception du courrier", "L'enregistrement des documents", "La distribution aux services concernés", "L'archivage des correspondances" ],
    highlight: "Une gestion rigoureuse du courrier permet de garantir la traçabilité des échanges avec les partenaires et les institutions.",
    narration: "La gestion du courrier comprend réception, enregistrement, distribution et archivage. Elle garantit la traçabilité des échanges." },

  // ===== S5 — Gestion documentaire =====
  { type: 'section', title: "5. Les principes de la gestion documentaire", narration: "Les principes de la gestion documentaire." },

  // SLIDE 18
  { type: 'content', title: "La gestion documentaire",
    intro: "La gestion documentaire correspond à l'ensemble des méthodes et outils permettant d'organiser la production, la diffusion, le classement et la conservation des documents.",
    intro2: "Dans le secteur social et médico-social, les documents peuvent concerner :",
    points: [ "Les dossiers des usagers", "Les documents administratifs", "Les documents réglementaires", "Les comptes rendus de réunions" ],
    highlight: "Une gestion documentaire efficace permet de faciliter l'accès aux informations tout en garantissant leur sécurité.",
    narration: "La gestion documentaire organise production, diffusion, classement et conservation. Elle facilite l'accès tout en garantissant la sécurité." },

  // ===== S6 — Archivage =====
  { type: 'section', title: "6. L'archivage physique et numérique des documents", narration: "L'archivage physique et numérique des documents." },

  // SLIDE 20
  { type: 'comparison', title: "Archivage physique vs Archivage numérique",
    wrong: [
      { label: "Physique : stockage papier volumineux", correct: "Numérique : gain d'espace considérable" },
      { label: "Physique : recherche manuelle longue", correct: "Numérique : recherche instantanée par mots-clés" },
      { label: "Physique : risque de détérioration", correct: "Numérique : meilleure traçabilité et durabilité" },
      { label: "Physique : accès limité au lieu de stockage", correct: "Numérique : accès à distance pour les autorisés" }
    ],
    highlight: "Les deux formes d'archivage peuvent coexister. L'archivage numérique nécessite cependant des systèmes informatiques sécurisés.",
    narration: "L'archivage physique et numérique ont chacun leurs avantages. Le numérique offre gain d'espace, recherche rapide et traçabilité, mais nécessite une sécurisation." },

  // ===== S7 — Durées de conservation =====
  { type: 'section', title: "7. Les durées légales de conservation des documents", narration: "Les durées légales de conservation des documents." },

  // SLIDE 22
  { type: 'content', title: "Les durées de conservation",
    intro: "La durée de conservation des documents est encadrée par la réglementation.",
    points: [ "Les dossiers des usagers doivent être conservés pendant une durée définie par les textes réglementaires", "Certains documents administratifs doivent être conservés pendant plusieurs années", "Les documents comptables ont des durées spécifiques de conservation (généralement 10 ans)", "Les contrats de travail doivent être conservés au minimum 5 ans après la fin du contrat" ],
    highlight: "Le responsable d'unité doit veiller au respect de ces obligations et organiser un système de gestion des durées de conservation.",
    narration: "La réglementation fixe les durées de conservation : dossiers usagers, documents administratifs, comptabilité (10 ans), contrats (5 ans après fin)." },

  // ===== S8 — Systèmes d'information =====
  { type: 'section', title: "8. Les systèmes d'information dans le secteur social", narration: "Les systèmes d'information dans le secteur social et médico-social." },

  // SLIDE 24
  { type: 'content', title: "Les systèmes d'information",
    intro: "Un système d'information correspond à l'ensemble des outils et des procédures permettant de collecter, traiter, stocker et diffuser les informations nécessaires au fonctionnement d'une organisation.",
    intro2: "Dans le secteur social et médico-social, les SI jouent un rôle essentiel pour :",
    points: [ "Le suivi des usagers", "La coordination des interventions", "La gestion administrative", "Le pilotage de l'activité" ],
    intro3: "Les SI doivent répondre à plusieurs exigences :",
    points2: [ "Fiabilité des données", "Sécurité des informations", "Accessibilité pour les professionnels autorisés", "Interopérabilité avec les systèmes partenaires" ],
    narration: "Les SI collectent, traitent et diffusent l'information. Ils doivent être fiables, sécurisés, accessibles et interopérables." },

  // ===== S9 — DUI =====
  { type: 'section', title: "9. Le dossier usager informatisé (DUI)", narration: "Le dossier usager informatisé." },

  // SLIDE 26
  { type: 'content', title: "Le Dossier Usager Informatisé (DUI)",
    intro: "Le DUI est un outil numérique permettant de centraliser les informations relatives à l'accompagnement des usagers.",
    intro2: "Il peut contenir :",
    points: [ "Les informations administratives", "Les projets personnalisés", "Les comptes rendus des interventions", "Les observations professionnelles" ],
    intro3: "L'utilisation du DUI permet :",
    points2: [ "D'améliorer la coordination entre professionnels", "De faciliter la traçabilité des actions", "De sécuriser les informations", "De garantir la continuité de l'accompagnement" ],
    narration: "Le DUI centralise informations administratives, projets personnalisés et observations. Il améliore coordination, traçabilité et sécurité." },

  // ===== S10 — Sécurité des données =====
  { type: 'section', title: "10. La sécurité des données et la protection des informations", narration: "La sécurité des données et la protection des informations." },

  // SLIDE 28
  { type: 'content', title: "La sécurité des données",
    intro: "Les professionnels doivent respecter plusieurs règles dans la gestion des données :",
    points: [ "Limiter l'accès aux informations aux personnes habilitées", "Ne pas divulguer d'informations confidentielles", "Sécuriser les documents administratifs", "Utiliser des mots de passe robustes", "Signaler tout incident de sécurité" ],
    highlight: "Ces règles participent à la protection de la vie privée des usagers et au respect du secret professionnel.",
    narration: "La sécurité des données impose de limiter les accès, protéger la confidentialité et sécuriser les documents." },

  // ===== S11 — RGPD =====
  { type: 'section', title: "11. Le Règlement Général sur la Protection des Données (RGPD)", narration: "Le RGPD." },

  // SLIDE 30
  { type: 'content', title: "Le cadre juridique du RGPD",
    intro: "Le RGPD est un règlement européen visant à protéger les informations relatives aux personnes physiques.",
    intro2: "Dans le secteur social et médico-social, les données concernent notamment :",
    points: [ "Les informations administratives des usagers", "Les données relatives à leur situation sociale ou médicale", "Les informations concernant les salariés" ],
    narration: "Le RGPD protège les données personnelles : informations administratives, sociales, médicales des usagers et données des salariés." },

  // SLIDE 31
  { type: 'grid', title: "Les principes fondamentaux du RGPD",
    items: [
      { num: "1", label: "Finalité — Les données ne doivent être collectées que pour un objectif précis et légitime" },
      { num: "2", label: "Minimisation — Seules les données strictement nécessaires doivent être collectées" },
      { num: "3", label: "Sécurité — Des mesures de protection doivent empêcher les accès non autorisés" },
      { num: "4", label: "Limitation de conservation — Les données ne peuvent être conservées au-delà de la durée nécessaire" },
      { num: "5", label: "Transparence — Les personnes doivent être informées de l'utilisation de leurs données" },
      { num: "6", label: "Responsabilité — L'organisation doit pouvoir démontrer sa conformité" }
    ],
    narration: "Les 6 principes du RGPD : finalité, minimisation, sécurité, limitation de conservation, transparence et responsabilité." },

  // ===== S12 — Droits des usagers =====
  { type: 'section', title: "12. Les droits des usagers concernant leurs données personnelles", narration: "Les droits des usagers concernant leurs données personnelles." },

  // SLIDE 33
  { type: 'content', title: "Les droits des personnes",
    intro: "Les personnes disposent de plusieurs droits concernant leurs données personnelles :",
    points: [ "Droit d'accès — consulter les informations les concernant", "Droit de rectification — corriger les données inexactes", "Droit à l'effacement — demander la suppression dans certaines situations", "Droit à la portabilité — récupérer ses données dans un format exploitable", "Droit d'opposition — s'opposer au traitement de ses données" ],
    highlight: "Le responsable d'unité doit veiller à ce que ces droits soient respectés et que les professionnels soient formés à ces obligations.",
    narration: "Les usagers ont des droits : accès, rectification, effacement, portabilité et opposition. Le responsable d'unité veille à leur respect." },

  // SLIDE 34 — Conclusion Chap 1
  { type: 'content', title: "Conclusion — Chapitre 1",
    intro: "La gestion administrative constitue une dimension essentielle du fonctionnement d'une unité d'intervention sociale.",
    intro2: "Le responsable d'unité doit être capable de :",
    points: [ "Organiser les processus administratifs", "Structurer la gestion documentaire", "Utiliser les systèmes d'information professionnels", "Garantir la protection des données personnelles" ],
    highlight: "Ces compétences permettent d'assurer la fiabilité des informations, la conformité réglementaire et la qualité du fonctionnement de l'unité.",
    narration: "La gestion administrative exige de maîtriser les processus, la gestion documentaire, les SI et la protection des données." },

  // =============================
  // CHAPITRE 2 — Logistique
  // =============================

  // SLIDE 35 — Transition
  { type: 'section', title: "Chapitre 2", subtitle: "Organiser la logistique d'une unité d'intervention sociale",
    narration: "Chapitre 2 : Organiser la logistique d'une unité d'intervention sociale." },

  // SLIDE 36 — Intro
  { type: 'content', title: "Introduction — Chapitre 2",
    intro: "La logistique regroupe l'ensemble des moyens matériels, techniques et organisationnels nécessaires au bon déroulement des activités du service.",
    intro2: "Une organisation logistique efficace permet :",
    points: [ "D'assurer des conditions d'accueil adaptées pour les usagers", "De garantir la sécurité des personnes et des biens", "De soutenir le travail des professionnels", "D'optimiser l'utilisation des ressources matérielles" ],
    highlight: "Le responsable d'unité joue un rôle central dans la coordination de ces dimensions logistiques, tout en intégrant les enjeux du développement durable.",
    narration: "La logistique assure conditions d'accueil, sécurité, soutien aux professionnels et optimisation des ressources." },

  // SLIDE 37 — Objectifs
  { type: 'content', title: "Objectifs pédagogiques — Chapitre 2",
    intro: "À l'issue de ce chapitre, le professionnel doit être capable de :",
    points: [ "Organiser la gestion logistique d'une unité", "Assurer l'entretien et la maintenance des locaux", "Gérer les équipements et le matériel du service", "Coordonner les achats et les stocks", "Intégrer les principes du développement durable" ],
    narration: "Objectifs : gestion logistique, entretien, équipements, achats/stocks et développement durable." },

  // SLIDE 38 — Sommaire Chap 2
  { type: 'grid', title: "Chapitre 2 — Sommaire détaillé",
    items: [
      { num: "1", label: "Les enjeux de la gestion logistique" },
      { num: "2", label: "La gestion et l'entretien des locaux" },
      { num: "3", label: "La maintenance préventive et corrective" },
      { num: "4", label: "Les normes ERP (Établissements Recevant du Public)" },
      { num: "5", label: "La gestion des équipements et du matériel" },
      { num: "6", label: "L'inventaire et le renouvellement" },
      { num: "7", label: "Le parc informatique et les outils numériques" },
      { num: "8", label: "La gestion des véhicules de service" },
      { num: "9", label: "Les procédures d'achats et les fournisseurs" },
      { num: "10", label: "La gestion et le suivi des stocks" },
      { num: "11", label: "L'optimisation des ressources matérielles" },
      { num: "12", label: "La RSE (Responsabilité Sociétale)" },
      { num: "13", label: "Le développement durable" },
      { num: "14", label: "Les éco-gestes et la gestion des déchets" }
    ],
    narration: "Le Chapitre 2 couvre 14 thèmes : enjeux logistiques, locaux, maintenance, ERP, équipements, inventaire, informatique, véhicules, achats, stocks, optimisation, RSE, développement durable et éco-gestes." },

  // ===== S1 — Gestion des locaux =====
  { type: 'section', title: "1. La gestion et l'entretien des locaux", narration: "La gestion et l'entretien des locaux." },

  // SLIDE 40
  { type: 'content', title: "La gestion des locaux",
    intro: "La gestion des locaux correspond à l'ensemble des actions visant à assurer l'entretien, la sécurité et la fonctionnalité des espaces.",
    intro2: "Les locaux doivent répondre à plusieurs exigences :",
    points: [ "Sécurité des personnes", "Accessibilité des espaces", "Confort des usagers et des professionnels", "Conformité aux normes réglementaires" ],
    narration: "La gestion des locaux assure entretien, sécurité et fonctionnalité. Les espaces doivent être sûrs, accessibles, confortables et conformes." },

  // SLIDE 41 — Entretien
  { type: 'content', title: "L'entretien des locaux",
    intro: "L'entretien des locaux contribue à maintenir des conditions d'hygiène et de sécurité satisfaisantes.",
    intro2: "Il peut inclure :",
    points: [ "Le nettoyage des espaces", "La désinfection de certains équipements", "L'entretien des installations techniques" ],
    highlight: "Le responsable d'unité doit s'assurer que ces prestations sont correctement réalisées et répondent aux besoins du service.",
    narration: "L'entretien inclut nettoyage, désinfection et maintenance des installations. Le responsable d'unité supervise ces prestations." },

  // ===== S2 — Maintenance =====
  { type: 'section', title: "2. La maintenance préventive et corrective des installations", narration: "La maintenance préventive et corrective." },

  // SLIDE 43
  { type: 'comparison', title: "Maintenance préventive vs Maintenance corrective",
    wrong: [
      { label: "Corrective : intervient après la panne", correct: "Préventive : intervient avant la panne pour l'éviter" },
      { label: "Corrective : coûts souvent plus élevés", correct: "Préventive : maîtrise des coûts sur le long terme" },
      { label: "Corrective : interruption de service possible", correct: "Préventive : continuité de service garantie" },
      { label: "Corrective : réparation d'urgence", correct: "Préventive : vérifications régulières planifiées" }
    ],
    highlight: "La maintenance préventive (vérification électrique, contrôle sécurité) est à privilégier. La corrective (réparations, interventions) reste nécessaire en cas de dysfonctionnement.",
    narration: "La maintenance préventive anticipe les pannes, la corrective intervient après. La préventive est à privilégier pour maîtriser les coûts et garantir la continuité." },

  // ===== S3 — Normes ERP =====
  { type: 'section', title: "3. Les normes de sécurité des ERP", narration: "Les normes de sécurité des Établissements Recevant du Public." },

  // SLIDE 45
  { type: 'content', title: "Les normes ERP",
    intro: "Les établissements sociaux et médico-sociaux sont souvent classés comme Établissements Recevant du Public (ERP).",
    intro2: "Les ERP doivent respecter des règles visant à garantir :",
    points: [ "La sécurité des personnes", "L'accessibilité des locaux" ],
    intro3: "Ces normes concernent notamment :",
    points2: [ "La prévention des incendies", "Les dispositifs d'évacuation", "L'accessibilité pour les personnes en situation de handicap", "La signalétique de sécurité", "Les vérifications périodiques obligatoires" ],
    highlight: "Le responsable d'unité doit veiller au respect de ces obligations et signaler toute anomalie à la direction ou aux services compétents.",
    narration: "Les ERP doivent respecter sécurité et accessibilité : prévention incendie, évacuation, accessibilité handicap, signalétique et vérifications." },

  // ===== S4 — Équipements =====
  { type: 'section', title: "4. La gestion des équipements et du matériel", narration: "La gestion des équipements et du matériel professionnel." },

  // SLIDE 47
  { type: 'content', title: "La gestion des équipements",
    intro: "La gestion des équipements correspond à l'ensemble des actions visant à assurer la disponibilité, l'entretien et le renouvellement du matériel.",
    intro2: "Les équipements peuvent inclure :",
    points: [ "Le matériel éducatif", "Le matériel médical", "Le matériel informatique", "Les véhicules de service", "Le mobilier professionnel" ],
    narration: "La gestion des équipements assure disponibilité, entretien et renouvellement du matériel éducatif, médical, informatique et des véhicules." },

  // ===== S5 — Inventaire =====
  { type: 'section', title: "5. L'inventaire et le renouvellement des équipements", narration: "L'inventaire et le renouvellement des équipements." },

  // SLIDE 49
  { type: 'content', title: "L'inventaire du matériel",
    intro: "L'inventaire consiste à établir une liste détaillée des équipements disponibles dans l'unité.",
    intro2: "Cet inventaire permet :",
    points: [ "De connaître les ressources matérielles disponibles", "De suivre l'état des équipements", "D'anticiper les besoins de renouvellement" ],
    intro3: "Le renouvellement implique :",
    points2: [ "Identifier les équipements obsolètes", "Anticiper les besoins de remplacement", "Participer à la planification des investissements" ],
    narration: "L'inventaire liste les équipements pour suivre leur état et anticiper le renouvellement." },

  // ===== S6 — Parc informatique =====
  { type: 'section', title: "6. Le parc informatique et les outils numériques", narration: "La gestion du parc informatique et des outils numériques." },

  // SLIDE 51
  { type: 'content', title: "La gestion du parc informatique",
    intro: "Le parc informatique comprend l'ensemble des équipements numériques utilisés par les professionnels :",
    points: [ "Ordinateurs et tablettes", "Logiciels métiers (DUI, plannings, comptabilité)", "Outils de communication (messagerie, visioconférence)", "Réseaux et connexions internet" ],
    intro2: "Le responsable d'unité doit veiller à :",
    points2: [ "La mise à jour des logiciels", "La sécurité des équipements", "La bonne utilisation des outils numériques", "La formation des professionnels aux nouveaux outils" ],
    narration: "Le parc informatique inclut ordinateurs, logiciels et outils de communication. Le responsable veille aux mises à jour, à la sécurité et à la formation." },

  // ===== S7 — Véhicules =====
  { type: 'section', title: "7. La gestion des véhicules de service", narration: "La gestion des véhicules de service." },

  // SLIDE 53
  { type: 'content', title: "La gestion des véhicules de service",
    intro: "Certaines unités disposent de véhicules permettant d'assurer les déplacements professionnels.",
    intro2: "La gestion de ces véhicules implique :",
    points: [ "Le suivi de leur entretien régulier", "La planification de leur utilisation", "La vérification des assurances et des contrôles techniques", "La tenue d'un carnet de bord", "Le respect du code de la route et des règles de sécurité" ],
    narration: "La gestion des véhicules implique entretien, planification, assurances, carnet de bord et respect des règles." },

  // ===== S8 — Achats et fournisseurs =====
  { type: 'section', title: "8. Les procédures d'achats et la relation avec les fournisseurs", narration: "Les procédures d'achats et la relation avec les fournisseurs." },

  // SLIDE 55
  { type: 'content', title: "La gestion des achats",
    intro: "La gestion des achats consiste à organiser l'approvisionnement en matériel et en fournitures nécessaires au fonctionnement du service.",
    intro2: "Elle implique plusieurs étapes :",
    points: [ "L'identification des besoins", "La sélection des fournisseurs", "La comparaison des offres", "La gestion des commandes", "Le suivi des livraisons" ],
    highlight: "Une bonne relation avec les fournisseurs permet d'assurer la continuité des approvisionnements et de négocier des conditions favorables.",
    narration: "La gestion des achats organise l'approvisionnement : identification des besoins, sélection fournisseurs, commandes et suivi." },

  // ===== S9 — Stocks =====
  { type: 'section', title: "9. La gestion et le suivi des stocks", narration: "La gestion et le suivi des stocks." },

  // SLIDE 57
  { type: 'content', title: "La gestion des stocks",
    intro: "La gestion des stocks consiste à suivre les quantités de matériel disponibles afin d'éviter les ruptures ou les surstocks.",
    intro2: "Les stocks peuvent concerner :",
    points: [ "Les fournitures de bureau", "Les produits alimentaires", "Le matériel médical", "Les produits d'entretien" ],
    intro3: "Une gestion efficace repose sur :",
    points2: [ "Le suivi des consommations", "L'anticipation des besoins", "La planification des commandes", "L'utilisation d'outils de suivi (tableaux, logiciels, inventaires réguliers)" ],
    narration: "La gestion des stocks évite ruptures et surstocks. Elle repose sur le suivi des consommations, l'anticipation et la planification." },

  // ===== S10 — RSE =====
  { type: 'section', title: "10. La responsabilité sociétale des organisations (RSE)", narration: "La responsabilité sociétale des organisations." },

  // SLIDE 59
  { type: 'content', title: "La RSE dans le secteur social",
    intro: "La Responsabilité Sociétale des Organisations (RSE) correspond à l'intégration des enjeux sociaux et environnementaux dans les activités d'une organisation.",
    intro2: "Dans le secteur social, la RSE vise notamment à :",
    points: [ "Réduire l'impact environnemental des activités", "Promouvoir des pratiques responsables", "Sensibiliser les professionnels aux enjeux écologiques", "Favoriser l'économie circulaire dans les approvisionnements" ],
    narration: "La RSE intègre les enjeux sociaux et environnementaux : réduction d'impact, pratiques responsables, sensibilisation et économie circulaire." },

  // ===== S11 — Développement durable =====
  { type: 'section', title: "11. Le développement durable dans les établissements sociaux", narration: "Le développement durable dans les établissements sociaux." },

  // SLIDE 61
  { type: 'grid', title: "Les trois piliers du développement durable",
    items: [
      { num: "E", label: "Dimension environnementale — Réduction de la consommation d'énergie, gestion des déchets, préservation des ressources" },
      { num: "S", label: "Dimension sociale — Bien-être des professionnels, conditions de travail, inclusion, qualité de l'accompagnement" },
      { num: "Ec", label: "Dimension économique — Optimisation des coûts, gestion responsable des ressources, pérennité du service" }
    ],
    narration: "Le développement durable repose sur trois piliers : environnemental, social et économique." },

  // ===== S12 — Éco-gestes =====
  { type: 'section', title: "12. Les éco-gestes et la gestion des déchets", narration: "Les éco-gestes et la gestion des déchets." },

  // SLIDE 63
  { type: 'content', title: "Les éco-gestes dans les établissements",
    intro: "Les éco-gestes correspondent à des pratiques simples permettant de réduire l'impact environnemental.",
    intro2: "Exemples :",
    points: [ "Réduction de la consommation d'énergie (éteindre les lumières, réguler le chauffage)", "Limitation du gaspillage alimentaire", "Réduction de l'utilisation du papier (dématérialisation)", "Tri sélectif et recyclage des matériaux", "Utilisation de produits écoresponsables" ],
    highlight: "Le responsable d'unité peut sensibiliser les professionnels à ces pratiques et intégrer les éco-gestes dans le fonctionnement du service.",
    narration: "Les éco-gestes : réduction d'énergie, limitation du gaspillage, dématérialisation, tri et produits écoresponsables." },

  // SLIDE 64 — Conclusion Chap 2
  { type: 'content', title: "Conclusion — Chapitre 2",
    intro: "L'organisation logistique constitue un levier essentiel pour garantir le bon fonctionnement du service.",
    intro2: "Le responsable d'unité doit être capable de :",
    points: [ "Assurer la gestion des locaux et des équipements", "Coordonner les achats et les stocks", "Garantir la sécurité des installations", "Intégrer les enjeux du développement durable" ],
    highlight: "Une gestion logistique efficace contribue à améliorer les conditions de travail des professionnels et la qualité de l'accueil des usagers.",
    narration: "La logistique implique gestion des locaux, équipements, achats, stocks, sécurité et développement durable." },

  // =============================
  // CHAPITRE 3 — Élaborer le budget
  // =============================

  // SLIDE 65 — Transition
  { type: 'section', title: "Chapitre 3", subtitle: "Élaborer et suivre le budget d'une unité d'intervention sociale",
    narration: "Chapitre 3 : Élaborer et suivre le budget d'une unité d'intervention sociale." },

  // SLIDE 66 — Intro
  { type: 'content', title: "Introduction — Chapitre 3",
    intro: "La gestion budgétaire constitue un enjeu majeur pour garantir la pérennité des services et la qualité de l'accompagnement. Les ressources financières étant limitées, les responsables d'unité doivent optimiser l'utilisation des moyens.",
    intro2: "Le responsable d'unité doit notamment :",
    points: [ "Identifier les besoins financiers liés au fonctionnement du service", "Participer à l'élaboration du budget", "Suivre l'utilisation des ressources", "Analyser les écarts entre prévisions et dépenses réelles" ],
    highlight: "La gestion budgétaire dans le secteur social s'inscrit dans un cadre réglementaire spécifique, lié aux modalités de tarification et aux financements publics.",
    narration: "La gestion budgétaire est un enjeu majeur. Le responsable d'unité identifie les besoins, participe au budget, suit les ressources et analyse les écarts." },

  // SLIDE 67 — Objectifs
  { type: 'content', title: "Objectifs pédagogiques — Chapitre 3",
    intro: "À l'issue de ce chapitre, le professionnel doit être capable de :",
    points: [ "Comprendre les principes fondamentaux de la gestion budgétaire", "Participer à l'élaboration du budget d'une unité", "Analyser les charges et les produits du service", "Comprendre les mécanismes de financement du secteur médico-social", "Contribuer à la planification des investissements" ],
    narration: "Objectifs : principes budgétaires, élaboration du budget, charges et produits, mécanismes de financement et planification des investissements." },

  // SLIDE 68 — Sommaire Chap 3
  { type: 'grid', title: "Chapitre 3 — Sommaire détaillé",
    items: [
      { num: "1", label: "Les principes fondamentaux de la gestion budgétaire" },
      { num: "2", label: "La structure du budget (charges et produits)" },
      { num: "3", label: "Les groupes fonctionnels budgétaires" },
      { num: "4", label: "Les charges de personnel, d'exploitation et de structure" },
      { num: "5", label: "Les amortissements et les investissements" },
      { num: "6", label: "Le processus de construction budgétaire" },
      { num: "7", label: "Les arbitrages budgétaires" },
      { num: "8", label: "Les mécanismes de financement (prix de journée, dotation)" },
      { num: "9", label: "Le CPOM et SERAFIN-PH" },
      { num: "10", label: "Le budget d'investissement et le PPI" }
    ],
    narration: "Le Chapitre 3 couvre 10 thèmes : principes budgétaires, structure, groupes fonctionnels, charges, amortissements, construction, arbitrages, financement, CPOM/SERAFIN et investissements." },

  // ===== S1 — Fondamentaux budgétaires =====
  { type: 'section', title: "1. Les principes fondamentaux de la gestion budgétaire", narration: "Les principes fondamentaux de la gestion budgétaire." },

  // SLIDE 70
  { type: 'content', title: "Définition du budget",
    intro: "Le budget peut être défini comme :",
    highlight: "Un document prévisionnel qui recense l'ensemble des dépenses et des recettes nécessaires au fonctionnement d'une organisation sur une période donnée.",
    intro2: "Dans le secteur social et médico-social, le budget permet de planifier l'utilisation des ressources financières afin de garantir la continuité et la qualité des services.",
    narration: "Le budget est un document prévisionnel des dépenses et recettes. Il planifie l'utilisation des ressources pour garantir continuité et qualité." },

  // ===== S2 — Charges et produits =====
  { type: 'section', title: "2. La structure du budget : charges et produits", narration: "La structure du budget : charges et produits." },

  // SLIDE 72
  { type: 'content', title: "Les charges",
    intro: "Les charges correspondent aux dépenses nécessaires au fonctionnement de l'établissement.",
    intro2: "Elles peuvent inclure :",
    points: [ "Les salaires des professionnels", "Les dépenses liées aux locaux", "Les achats de matériel", "Les frais de fonctionnement courant" ],
    highlight: "Dans les établissements médico-sociaux, les dépenses de personnel représentent généralement 70 à 80% du budget total.",
    narration: "Les charges sont les dépenses : salaires, locaux, matériel et fonctionnement. Les dépenses de personnel représentent 70 à 80% du budget." },

  // SLIDE 73
  { type: 'content', title: "Les produits",
    intro: "Les produits correspondent aux ressources financières permettant de financer l'activité.",
    intro2: "Ils peuvent provenir :",
    points: [ "Des financements publics (ARS, conseil départemental)", "Des dotations des autorités de tarification", "Des participations des usagers", "Des subventions et mécénats", "Des produits exceptionnels" ],
    highlight: "L'équilibre budgétaire repose sur la capacité de l'établissement à équilibrer ses charges et ses produits.",
    narration: "Les produits sont les ressources : financements publics, dotations, participations des usagers, subventions. L'équilibre charges/produits est essentiel." },

  // ===== S3 — Groupes fonctionnels =====
  { type: 'section', title: "3. Les groupes fonctionnels budgétaires", narration: "Les groupes fonctionnels budgétaires." },

  // SLIDE 75
  { type: 'grid', title: "Les 3 groupes fonctionnels de charges",
    items: [
      { num: "G1", label: "Charges de personnel — Salaires, charges sociales, formation, intérim. Représentent la part la plus importante du budget (70-80%)." },
      { num: "G2", label: "Charges d'exploitation — Alimentation, fournitures, énergie, entretien, transports. Dépenses liées au fonctionnement courant." },
      { num: "G3", label: "Charges de structure — Amortissements, frais financiers, charges administratives. Dépenses liées aux infrastructures." }
    ],
    narration: "Les trois groupes : G1 charges de personnel (70-80%), G2 charges d'exploitation (fonctionnement courant), G3 charges de structure (infrastructures)." },

  // ===== S4 — Amortissements =====
  { type: 'section', title: "4. Les amortissements et les investissements", narration: "Les amortissements et les investissements." },

  // SLIDE 77
  { type: 'content', title: "Les amortissements",
    intro: "L'amortissement correspond à la répartition du coût d'un investissement sur plusieurs années.",
    intro2: "Par exemple :",
    points: [ "L'achat d'un véhicule (amorti sur 5 à 7 ans)", "L'acquisition d'équipements informatiques (amorti sur 3 à 5 ans)", "La rénovation d'un bâtiment (amorti sur 15 à 30 ans)" ],
    highlight: "L'amortissement permet de prendre en compte l'usure progressive des équipements et de provisionner leur remplacement futur.",
    narration: "L'amortissement répartit le coût d'un investissement sur plusieurs années : véhicule 5-7 ans, informatique 3-5 ans, bâtiment 15-30 ans." },

  // ===== S5 — Construction budgétaire =====
  { type: 'section', title: "5. Le processus de construction budgétaire", narration: "Le processus de construction budgétaire." },

  // SLIDE 79
  { type: 'grid', title: "Les étapes de la construction budgétaire",
    items: [
      { num: "1", label: "Recueil des besoins — Identifier les besoins du service (RH, équipements, projets usagers)" },
      { num: "2", label: "Chiffrage — Estimer le coût des besoins identifiés et anticiper les évolutions" },
      { num: "3", label: "Arbitrages — Prioriser les dépenses, reporter certains investissements, ajuster les moyens" },
      { num: "4", label: "Validation — Approbation du budget par la direction et les autorités de tarification" }
    ],
    narration: "La construction budgétaire passe par 4 étapes : recueil des besoins, chiffrage, arbitrages et validation." },

  // ===== S6 — Mécanismes de financement =====
  { type: 'section', title: "6. Les mécanismes de financement du secteur médico-social", narration: "Les mécanismes de financement du secteur médico-social." },

  // SLIDE 81
  { type: 'content', title: "Le prix de journée",
    intro: "Le prix de journée correspond au montant versé par l'autorité de tarification pour chaque jour de présence d'un usager dans l'établissement.",
    highlight: "Ce système de financement lie directement les ressources de l'établissement au taux d'occupation. Une baisse d'occupation entraîne une baisse de financement.",
    narration: "Le prix de journée est le montant versé par jour de présence d'un usager. Les ressources sont liées au taux d'occupation." },

  // SLIDE 82
  { type: 'content', title: "La dotation globale de financement",
    intro: "La dotation globale correspond à un financement forfaitaire attribué à l'établissement pour couvrir l'ensemble de ses dépenses.",
    highlight: "Ce financement est généralement fixé pour une période donnée, offrant plus de stabilité que le prix de journée.",
    narration: "La dotation globale est un financement forfaitaire fixé pour une période, offrant plus de stabilité que le prix de journée." },

  // SLIDE 83
  { type: 'content', title: "Le CPOM (Contrat Pluriannuel d'Objectifs et de Moyens)",
    intro: "Le CPOM constitue un outil de contractualisation entre l'établissement et les autorités de tarification.",
    intro2: "Il définit :",
    points: [ "Les objectifs stratégiques", "Les moyens financiers", "Les indicateurs de suivi", "La durée de l'engagement (généralement 5 ans)" ],
    highlight: "Le CPOM permet d'inscrire le financement dans une logique pluriannuelle et de sécuriser les ressources de l'établissement.",
    narration: "Le CPOM est un contrat pluriannuel qui définit objectifs, moyens financiers et indicateurs. Il sécurise les ressources sur 5 ans." },

  // SLIDE 84
  { type: 'content', title: "SERAFIN-PH : la réforme du financement handicap",
    intro: "Le programme SERAFIN-PH (Services et Établissements : Réforme pour une Adéquation des Financements aux parcours des Personnes Handicapées) vise à réformer les modalités de financement.",
    intro2: "L'objectif est de :",
    points: [ "Mieux adapter les financements aux besoins des personnes", "Favoriser une approche centrée sur les parcours des usagers", "Harmoniser les modalités de tarification", "Développer de nouveaux outils d'analyse des besoins et des coûts" ],
    narration: "SERAFIN-PH réforme le financement du handicap pour mieux adapter les financements aux besoins et favoriser une approche par les parcours." },

  // ===== S7 — Budget d'investissement =====
  { type: 'section', title: "7. Le budget d'investissement et le PPI", narration: "Le budget d'investissement et le plan pluriannuel d'investissement." },

  // SLIDE 86
  { type: 'content', title: "Le budget d'investissement",
    intro: "Le budget d'investissement concerne les dépenses liées à l'acquisition ou à la rénovation d'équipements durables.",
    intro2: "Ces investissements peuvent concerner :",
    points: [ "Les bâtiments (construction, rénovation)", "Les équipements techniques", "Les systèmes informatiques", "Les véhicules" ],
    highlight: "Contrairement aux dépenses de fonctionnement, les investissements s'inscrivent dans une perspective à long terme.",
    narration: "Le budget d'investissement concerne bâtiments, équipements, informatique et véhicules. Il s'inscrit dans le long terme." },

  // SLIDE 87
  { type: 'content', title: "Le Plan Pluriannuel d'Investissement (PPI)",
    intro: "Le PPI permet de planifier les investissements sur plusieurs années.",
    intro2: "Il vise à :",
    points: [ "Anticiper les besoins futurs", "Répartir les dépenses dans le temps", "Garantir la cohérence des projets", "Prioriser les investissements stratégiques" ],
    highlight: "Le responsable d'unité peut contribuer à l'élaboration du PPI en identifiant les besoins liés au fonctionnement de son service.",
    narration: "Le PPI planifie les investissements sur plusieurs années : anticipation, répartition, cohérence et priorisation." },

  // SLIDE 88 — Conclusion Chap 3
  { type: 'content', title: "Conclusion — Chapitre 3",
    intro: "La gestion budgétaire constitue une compétence essentielle pour le responsable d'unité.",
    intro2: "Il doit être capable de :",
    points: [ "Comprendre les mécanismes budgétaires", "Participer à l'élaboration du budget", "Analyser les dépenses et les ressources", "Contribuer à la planification des investissements" ],
    highlight: "Cette maîtrise des enjeux financiers permet de garantir une utilisation responsable des ressources et de soutenir la qualité des services.",
    narration: "La gestion budgétaire exige de comprendre les mécanismes, participer à l'élaboration, analyser les dépenses et planifier les investissements." },

  // =============================
  // CHAPITRE 4 — Exécution et suivi du budget
  // =============================

  // SLIDE 89 — Transition
  { type: 'section', title: "Chapitre 4", subtitle: "Gérer l'exécution et le suivi du budget",
    narration: "Chapitre 4 : Gérer l'exécution et le suivi du budget." },

  // SLIDE 90 — Intro
  { type: 'content', title: "Introduction — Chapitre 4",
    intro: "La gestion budgétaire ne se limite pas à l'élaboration du budget prévisionnel. Elle implique un suivi régulier de l'exécution budgétaire.",
    intro2: "Le suivi budgétaire permet de :",
    points: [ "Contrôler l'utilisation des ressources", "Anticiper les difficultés financières", "Ajuster les dépenses en fonction de l'activité", "Garantir la transparence de la gestion financière" ],
    highlight: "La gestion budgétaire s'inscrit dans un cadre réglementaire spécifique, notamment à travers l'EPRD et l'ERRD.",
    narration: "Le suivi budgétaire contrôle les ressources, anticipe les difficultés, ajuste les dépenses et garantit la transparence." },

  // SLIDE 91 — Objectifs
  { type: 'content', title: "Objectifs pédagogiques — Chapitre 4",
    intro: "À l'issue de ce chapitre, le professionnel doit être capable de :",
    points: [ "Comprendre les outils de suivi budgétaire", "Analyser l'exécution du budget au cours de l'année", "Identifier les écarts entre prévisions et dépenses réelles", "Contribuer à l'élaboration des documents financiers réglementaires", "Participer au pilotage financier de l'unité" ],
    narration: "Objectifs : outils de suivi, analyse de l'exécution, identification des écarts, documents financiers et pilotage." },

  // SLIDE 92 — Sommaire
  { type: 'grid', title: "Chapitre 4 — Sommaire détaillé",
    items: [
      { num: "1", label: "Le suivi infra-annuel du budget" },
      { num: "2", label: "Les outils de suivi financier" },
      { num: "3", label: "La balance comptable et le grand livre" },
      { num: "4", label: "Les tableaux de suivi budgétaire" },
      { num: "5", label: "Le contrôle de gestion" },
      { num: "6", label: "L'analyse des écarts budgétaires" },
      { num: "7", label: "Les mesures correctives et ajustements" },
      { num: "8", label: "Les indicateurs financiers de pilotage" },
      { num: "9", label: "L'EPRD (État Prévisionnel)" },
      { num: "10", label: "L'ERRD (État Réalisé)" },
      { num: "11", label: "L'articulation EPRD / ERRD" }
    ],
    narration: "Le Chapitre 4 couvre 11 thèmes : suivi infra-annuel, outils financiers, balance, tableaux, contrôle de gestion, écarts, mesures correctives, indicateurs, EPRD, ERRD et articulation." },

  // ===== S1 — Suivi infra-annuel =====
  { type: 'section', title: "1. Le suivi infra-annuel du budget", narration: "Le suivi infra-annuel du budget." },

  // SLIDE 94
  { type: 'content', title: "Le suivi infra-annuel",
    intro: "Le suivi infra-annuel correspond à l'analyse régulière de l'exécution budgétaire au cours de l'année.",
    intro2: "Il permet de vérifier si les dépenses et recettes évoluent conformément aux prévisions. Ce suivi est généralement réalisé :",
    points: [ "Mensuellement", "Trimestriellement", "Semestriellement" ],
    highlight: "Le suivi infra-annuel permet d'anticiper d'éventuels déséquilibres financiers et d'adapter la gestion du service.",
    narration: "Le suivi infra-annuel vérifie régulièrement l'exécution budgétaire. Il peut être mensuel, trimestriel ou semestriel." },

  // ===== S2 — Outils de suivi =====
  { type: 'section', title: "2. Les outils de suivi financier", narration: "Les outils de suivi financier." },

  // SLIDE 96
  { type: 'content', title: "La balance comptable",
    intro: "La balance comptable est un document qui présente l'ensemble des comptes de l'établissement.",
    intro2: "Elle permet de visualiser :",
    points: [ "Les montants des dépenses", "Les montants des recettes", "L'équilibre global des comptes" ],
    highlight: "La balance constitue un outil essentiel pour suivre la situation financière d'une organisation.",
    narration: "La balance comptable présente tous les comptes : dépenses, recettes et équilibre global." },

  // SLIDE 97
  { type: 'content', title: "Le grand livre",
    intro: "Le grand livre est un document comptable qui regroupe l'ensemble des opérations financières enregistrées.",
    intro2: "Il permet d'analyser en détail :",
    points: [ "Les dépenses réalisées", "Les recettes perçues", "Les mouvements financiers" ],
    highlight: "Le grand livre constitue une source d'information importante pour comprendre l'évolution des dépenses.",
    narration: "Le grand livre regroupe toutes les opérations financières : dépenses, recettes et mouvements." },

  // SLIDE 98
  { type: 'content', title: "Les tableaux de suivi des dépenses",
    intro: "Les tableaux de suivi sont des outils de pilotage permettant de visualiser l'évolution des dépenses par rapport aux prévisions.",
    intro2: "Ils peuvent inclure :",
    points: [ "Le budget initial prévu", "Les dépenses réalisées à date", "Les écarts entre prévisions et réalisations", "Les projections de fin d'année" ],
    highlight: "Ces tableaux facilitent la prise de décision et l'ajustement des dépenses en cours d'exercice.",
    narration: "Les tableaux de suivi visualisent budget initial, dépenses réalisées, écarts et projections. Ils facilitent la prise de décision." },

  // ===== S3 — Contrôle de gestion =====
  { type: 'section', title: "3. Le contrôle de gestion dans les établissements sociaux", narration: "Le contrôle de gestion dans les établissements sociaux." },

  // SLIDE 100
  { type: 'content', title: "Le contrôle de gestion",
    intro: "Le contrôle de gestion correspond à l'ensemble des méthodes et outils permettant d'analyser l'utilisation des ressources financières afin d'améliorer la performance.",
    intro2: "Dans le secteur social, il vise à :",
    points: [ "Optimiser l'utilisation des ressources", "Améliorer l'efficacité des services", "Garantir la transparence de la gestion financière" ],
    narration: "Le contrôle de gestion analyse l'utilisation des ressources pour optimiser, améliorer l'efficacité et garantir la transparence." },

  // ===== S4 — Analyse des écarts =====
  { type: 'section', title: "4. L'analyse des écarts budgétaires", narration: "L'analyse des écarts budgétaires." },

  // SLIDE 102
  { type: 'content', title: "L'analyse des écarts",
    intro: "L'analyse des écarts consiste à comparer les résultats réels avec les prévisions budgétaires.",
    intro2: "Elle permet d'identifier :",
    points: [ "Les dépassements de budget", "Les économies réalisées", "Les évolutions inattendues des dépenses" ],
    intro3: "Les causes d'écarts peuvent être :",
    points2: [ "Une évolution imprévue de l'activité", "Des dépenses exceptionnelles", "Des variations dans les besoins des usagers", "Des erreurs d'estimation initiale" ],
    narration: "L'analyse des écarts compare résultats réels et prévisions. Les causes : évolution d'activité, dépenses exceptionnelles, variations de besoins." },

  // ===== S5 — Mesures correctives =====
  { type: 'section', title: "5. Les mesures correctives et les ajustements", narration: "Les mesures correctives et les ajustements financiers." },

  // SLIDE 104
  { type: 'content', title: "Les mesures correctives",
    intro: "Lorsque des écarts importants sont constatés, il peut être nécessaire de mettre en place des mesures correctives.",
    intro2: "Ces mesures peuvent inclure :",
    points: [ "La réduction de certaines dépenses", "La réorganisation de certaines activités", "L'ajustement des ressources", "Le report de certains investissements" ],
    highlight: "Le responsable d'unité peut être amené à proposer des ajustements afin de maintenir l'équilibre budgétaire.",
    narration: "Les mesures correctives : réduction de dépenses, réorganisation, ajustement des ressources et report d'investissements." },

  // ===== S6 — Indicateurs =====
  { type: 'section', title: "6. Les indicateurs financiers de pilotage", narration: "Les indicateurs financiers de pilotage." },

  // SLIDE 106
  { type: 'grid', title: "Les indicateurs financiers clés",
    items: [
      { num: "1", label: "Coût par usager — Dépense moyenne par personne accompagnée" },
      { num: "2", label: "Taux d'occupation — Rapport entre capacité utilisée et capacité totale" },
      { num: "3", label: "Dépenses par activité — Répartition des coûts par type d'intervention" },
      { num: "4", label: "Ratio charges de personnel — Part des salaires dans le budget total" },
      { num: "5", label: "Taux d'exécution budgétaire — Dépenses réalisées / budget prévu" },
      { num: "6", label: "Résultat comptable — Différence entre produits et charges" }
    ],
    narration: "Les indicateurs clés : coût par usager, taux d'occupation, dépenses par activité, ratio personnel, taux d'exécution et résultat comptable." },

  // ===== S7 — EPRD =====
  { type: 'section', title: "7. L'EPRD (État Prévisionnel des Recettes et des Dépenses)", narration: "L'EPRD." },

  // SLIDE 108
  { type: 'content', title: "L'EPRD",
    intro: "L'EPRD constitue le document budgétaire prévisionnel des établissements sociaux et médico-sociaux.",
    intro2: "L'EPRD présente :",
    points: [ "Les recettes prévues", "Les dépenses prévues", "Les objectifs financiers de l'établissement" ],
    highlight: "Ce document est transmis aux autorités de tarification. Il remplace progressivement les anciens budgets prévisionnels.",
    narration: "L'EPRD est le document prévisionnel des établissements. Il présente recettes, dépenses et objectifs financiers." },

  // ===== S8 — ERRD =====
  { type: 'section', title: "8. L'ERRD (État Réalisé des Recettes et des Dépenses)", narration: "L'ERRD." },

  // SLIDE 110
  { type: 'content', title: "L'ERRD",
    intro: "L'ERRD présente les résultats financiers réels de l'établissement à la fin de l'exercice.",
    intro2: "Il permet de :",
    points: [ "Comparer les prévisions et les réalisations", "Analyser les écarts budgétaires", "Rendre compte de la gestion financière" ],
    highlight: "L'ERRD est transmis aux autorités de contrôle et permet d'évaluer la performance financière.",
    narration: "L'ERRD présente les résultats réels. Il compare prévisions et réalisations et rend compte de la gestion." },

  // SLIDE 111 — Comparaison EPRD/ERRD
  { type: 'comparison', title: "EPRD vs ERRD",
    wrong: [
      { label: "EPRD : document prévisionnel (avant l'exercice)", correct: "ERRD : bilan financier réel (après l'exercice)" },
      { label: "EPRD : recettes et dépenses prévues", correct: "ERRD : recettes et dépenses réellement réalisées" },
      { label: "EPRD : objectifs financiers", correct: "ERRD : résultats financiers constatés" },
      { label: "EPRD : soumis pour approbation", correct: "ERRD : soumis pour contrôle et vérification" }
    ],
    highlight: "L'EPRD et l'ERRD sont deux documents complémentaires. Leur analyse conjointe permet d'évaluer la performance financière de l'établissement.",
    narration: "L'EPRD est le prévisionnel, l'ERRD le bilan réel. Ensemble, ils évaluent la performance financière." },

  // SLIDE 112 — Conclusion Chap 4
  { type: 'content', title: "Conclusion — Chapitre 4",
    intro: "La gestion et le suivi du budget constituent des compétences essentielles pour le responsable d'unité.",
    intro2: "La compréhension des outils budgétaires permet de :",
    points: [ "Suivre l'évolution des dépenses", "Analyser les résultats financiers", "Ajuster les actions du service", "Contribuer à la transparence financière" ],
    highlight: "Cette maîtrise contribue à garantir une utilisation efficace des ressources et à soutenir la qualité de l'accompagnement des usagers.",
    narration: "Le suivi budgétaire permet de suivre les dépenses, analyser les résultats et ajuster les actions pour une utilisation efficace des ressources." },

  // =============================
  // CHAPITRE 5 — Communication gestion admin/budgétaire
  // =============================

  // SLIDE 113 — Transition
  { type: 'section', title: "Chapitre 5", subtitle: "Communiquer dans le cadre de la gestion administrative et budgétaire",
    narration: "Chapitre 5 : Communiquer dans le cadre de la gestion administrative et budgétaire." },

  // SLIDE 114 — Intro
  { type: 'content', title: "Introduction — Chapitre 5",
    intro: "La gestion administrative et budgétaire ne peut être efficace sans une communication claire et adaptée. Le responsable d'unité occupe une position d'interface entre :",
    points: [ "L'équipe professionnelle", "La direction de l'établissement", "Les autorités de tarification et les financeurs", "Les partenaires institutionnels" ],
    highlight: "Le responsable d'unité doit être capable de traduire des informations financières parfois complexes en messages accessibles, tout en justifiant les résultats auprès des autorités de contrôle.",
    narration: "La communication financière est essentielle. Le responsable d'unité traduit l'information financière pour les équipes et justifie les résultats auprès des autorités." },

  // SLIDE 115 — Objectifs
  { type: 'content', title: "Objectifs pédagogiques — Chapitre 5",
    intro: "À l'issue de ce chapitre, le professionnel doit être capable de :",
    points: [ "Expliquer les enjeux budgétaires à une équipe professionnelle", "Présenter les informations financières de manière pédagogique", "Rendre compte de l'utilisation des ressources auprès de la direction", "Justifier les écarts budgétaires auprès des autorités de tarification" ],
    narration: "Objectifs : expliquer les enjeux, présenter les informations financières, rendre compte et justifier les écarts." },

  // SLIDE 116 — Sommaire
  { type: 'grid', title: "Chapitre 5 — Sommaire détaillé",
    items: [
      { num: "1", label: "Les principes de la communication financière" },
      { num: "2", label: "Les enjeux de la transparence budgétaire" },
      { num: "3", label: "La pédagogie budgétaire auprès des équipes" },
      { num: "4", label: "Présenter les informations financières" },
      { num: "5", label: "Expliquer les contraintes budgétaires" },
      { num: "6", label: "Développer une culture de gestion" },
      { num: "7", label: "La reddition de comptes" },
      { num: "8", label: "Les obligations de reporting" },
      { num: "9", label: "Justifier les écarts budgétaires" },
      { num: "10", label: "Les rapports financiers et tableaux de synthèse" },
      { num: "11", label: "Les principes d'une communication efficace" }
    ],
    narration: "Le Chapitre 5 couvre 11 thèmes : communication financière, transparence, pédagogie, présentation, contraintes, culture de gestion, reddition, reporting, écarts, rapports et principes." },

  // ===== S1 — Communication financière =====
  { type: 'section', title: "1. Les principes de la communication financière", narration: "Les principes de la communication financière." },

  // SLIDE 118
  { type: 'content', title: "La communication financière",
    intro: "La communication financière correspond à l'ensemble des actions visant à transmettre des informations relatives à la gestion économique et budgétaire.",
    intro2: "Elle peut concerner :",
    points: [ "La présentation du budget du service", "L'explication des contraintes financières", "L'information sur l'utilisation des ressources" ],
    highlight: "Le responsable d'unité doit adapter son discours en fonction de ses interlocuteurs afin de rendre ces informations compréhensibles.",
    narration: "La communication financière transmet les informations budgétaires. Le responsable doit adapter son discours selon ses interlocuteurs." },

  // ===== S2 — Transparence =====
  { type: 'section', title: "2. Les enjeux de la transparence budgétaire", narration: "Les enjeux de la transparence budgétaire." },

  // SLIDE 120
  { type: 'content', title: "Transparence et sens des décisions",
    intro: "La transparence dans la gestion des ressources contribue à instaurer un climat de confiance au sein de l'équipe.",
    intro2: "Certaines décisions peuvent être difficiles à accepter :",
    points: [ "Limitation des dépenses", "Report d'un projet", "Réduction de certaines ressources" ],
    highlight: "Une communication claire permet d'expliquer les raisons de ces décisions et de favoriser leur compréhension. Elle responsabilise aussi les professionnels dans l'utilisation des ressources.",
    narration: "La transparence instaure la confiance. Elle permet d'expliquer les décisions difficiles et de responsabiliser les professionnels." },

  // ===== S3 — Pédagogie budgétaire =====
  { type: 'section', title: "3. La pédagogie budgétaire auprès des équipes", narration: "La pédagogie budgétaire auprès des équipes." },

  // SLIDE 122
  { type: 'content', title: "La pédagogie budgétaire",
    intro: "La pédagogie budgétaire consiste à rendre accessibles des informations financières parfois complexes.",
    intro2: "Cette démarche peut inclure :",
    points: [ "L'utilisation de graphiques ou de tableaux simplifiés", "L'explication des principales catégories de dépenses", "La mise en lien entre les ressources financières et les activités du service", "La comparaison avec des repères concrets compréhensibles" ],
    highlight: "L'objectif n'est pas de transmettre des informations techniques complexes, mais de permettre aux professionnels de comprendre les enjeux financiers.",
    narration: "La pédagogie budgétaire utilise graphiques, tableaux simplifiés et exemples concrets pour rendre les informations financières accessibles." },

  // ===== S4 — Reddition de comptes =====
  { type: 'section', title: "4. La reddition de comptes dans les organisations sociales", narration: "La reddition de comptes dans les organisations sociales." },

  // SLIDE 124
  { type: 'content', title: "La reddition de comptes",
    intro: "La reddition de comptes correspond au processus par lequel une organisation rend compte de l'utilisation des ressources qui lui sont confiées.",
    intro2: "Les interlocuteurs de la reddition de comptes :",
    points: [ "La direction de l'établissement", "Les organismes gestionnaires", "Les autorités de tarification (ARS, conseils départementaux)", "Les organismes de contrôle" ],
    highlight: "Le responsable d'unité peut être sollicité pour expliquer certains éléments liés à la gestion de son service.",
    narration: "La reddition de comptes rend compte de l'utilisation des ressources auprès de la direction, des gestionnaires et des autorités." },

  // ===== S5 — Justifier les écarts =====
  { type: 'section', title: "5. L'analyse et la justification des écarts budgétaires", narration: "La justification des écarts budgétaires." },

  // SLIDE 126
  { type: 'content', title: "Justifier les écarts budgétaires",
    intro: "Les écarts entre prévisions et dépenses réelles peuvent être liés à :",
    points: [ "Une évolution imprévue de l'activité", "Des dépenses exceptionnelles", "Des variations dans les besoins des usagers" ],
    intro2: "L'analyse des écarts consiste à identifier :",
    points2: [ "Les écarts entre dépenses prévues et réalisées", "Les causes de ces écarts", "Les actions correctives possibles" ],
    highlight: "Le responsable d'unité doit être en mesure d'expliquer ces écarts en s'appuyant sur des éléments factuels.",
    narration: "Les écarts peuvent être liés à l'activité, des dépenses exceptionnelles ou des variations de besoins. Il faut les analyser et les justifier." },

  // ===== S6 — Principes communication efficace =====
  { type: 'section', title: "6. Les principes d'une communication financière efficace", narration: "Les principes d'une communication financière efficace." },

  // SLIDE 128
  { type: 'grid', title: "Les 4 principes de la communication financière",
    items: [
      { num: "1", label: "Clarté — Les informations doivent être présentées de manière compréhensible" },
      { num: "2", label: "Précision — Les données doivent être fiables et vérifiables" },
      { num: "3", label: "Adaptation — Le discours doit être adapté au niveau de connaissance des interlocuteurs" },
      { num: "4", label: "Transparence — La communication doit refléter la réalité de la situation financière" }
    ],
    narration: "Les 4 principes : clarté, précision, adaptation au public et transparence." },

  // SLIDE 129 — Conclusion Chap 5
  { type: 'content', title: "Conclusion — Chapitre 5",
    intro: "La communication constitue un élément essentiel de la gestion administrative et budgétaire.",
    intro2: "Le responsable d'unité doit être capable de :",
    points: [ "Expliquer les enjeux budgétaires aux équipes", "Rendre compte de l'utilisation des ressources", "Justifier les écarts financiers auprès des autorités compétentes" ],
    highlight: "Cette compétence contribue à renforcer la transparence de la gestion et à favoriser une culture de responsabilité partagée.",
    narration: "La communication financière implique d'expliquer les enjeux, rendre compte et justifier les écarts. Elle renforce la transparence et la responsabilité." },

  // =============================
  // CONCLUSION DU BLOC 3
  // =============================

  // SLIDE 130
  { type: 'content', title: "Conclusion du Bloc 3",
    intro: "Synthèse des compétences administratives et financières du responsable d'unité :",
    points: [
      "Assurer la gestion administrative (documents, SI, RGPD)",
      "Organiser la logistique (locaux, équipements, stocks, RSE)",
      "Élaborer le budget (charges, produits, financement)",
      "Suivre l'exécution budgétaire (indicateurs, EPRD, ERRD)",
      "Communiquer sur la gestion (pédagogie, reddition de comptes)"
    ],
    highlight: "Le cadre intermédiaire joue un rôle essentiel dans le pilotage des moyens de l'établissement. Son action contribue directement à la qualité de l'accompagnement des usagers.",
    narration: "Le Bloc 3 synthétise les compétences administratives et financières : gestion, logistique, budget, suivi et communication." },

  // SLIDE 131 — Points clés
  { type: 'content', title: "Points clés du Bloc 3",
    points: [
      "Le RGPD et la protection des données personnelles des usagers",
      "Le DUI (Dossier Usager Informatisé) pour la coordination des interventions",
      "Les normes ERP pour la sécurité des établissements",
      "La RSE et le développement durable dans le secteur social",
      "Les 3 groupes fonctionnels de charges budgétaires",
      "Le CPOM et SERAFIN-PH pour le financement du secteur",
      "L'EPRD (prévisionnel) et l'ERRD (réalisé)",
      "La pédagogie budgétaire et la reddition de comptes"
    ],
    narration: "Points clés : RGPD, DUI, ERP, RSE, groupes fonctionnels, CPOM, SERAFIN-PH, EPRD, ERRD et pédagogie budgétaire." },

  // SLIDE 132 — Félicitations
  { type: 'congratulations', title: "Bloc 3 terminé !",
    message: "Vous avez parcouru l'ensemble du Bloc 3 : Gérer les volets administratif, logistique et budgétaire.",
    details: [
      "5 chapitres couverts",
      "Gestion administrative et systèmes d'information",
      "Organisation logistique et développement durable",
      "Élaboration et construction budgétaire",
      "Suivi de l'exécution et indicateurs financiers",
      "Communication financière et reddition de comptes"
    ],
    narration: "Félicitations ! Vous avez terminé le Bloc 3 du CAFERUIS. Rendez-vous dans le Quiz BC3 pour valider vos acquis !" }
];

const CAFBC3Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef(null);
  const progressPercent = Math.round(((currentSlide + 1) / slides.length) * 100);

  useEffect(() => { markSlideViewed(ACTIVITY_ID, currentSlide, slides.length); }, [currentSlide]);

  const nextSlide = useCallback(() => { if (currentSlide < slides.length - 1) { stopAudio(); setCurrentSlide(c => c + 1); } }, [currentSlide]);
  const prevSlide = useCallback(() => { if (currentSlide > 0) { stopAudio(); setCurrentSlide(c => c - 1); } }, [currentSlide]);
  const goToSlide = useCallback((idx) => { stopAudio(); setCurrentSlide(idx); }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'ArrowRight') nextSlide(); if (e.key === 'ArrowLeft') prevSlide(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [nextSlide, prevSlide]);

  const stopAudio = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } setIsPlaying(false); setIsLoading(false); };

  const getSlideText = (s) => {
    if (s.narration) return s.narration;
    let t = s.title || '';
    if (s.intro) t += '. ' + s.intro;
    if (s.points) t += '. ' + s.points.join('. ');
    return t;
  };

  const speakText = async (t) => {
    stopAudio();
    try {
      setIsLoading(true); setIsPlaying(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${API_URL}/api/tts/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: t, speed: 1.1 }) });
      if (!response.ok) throw new Error('TTS failed');
      const data = await response.json();
      const audioData = data.audio_base64 || data.audio;
      setIsLoading(false);
      if (audioData) {
        const audioBlob = new Blob([Uint8Array.from(atob(audioData), c => c.charCodeAt(0))], { type: 'audio/mp3' });
        const audioUrl = URL.createObjectURL(audioBlob);
        audioRef.current = new Audio(audioUrl);
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => { toast.error("Erreur de lecture audio"); setIsPlaying(false); };
        audioRef.current.play().catch(() => { toast.error("Impossible de lire l'audio"); setIsPlaying(false); });
      } else { toast.error("Pas de données audio reçues"); setIsPlaying(false); }
    } catch { toast.error("Erreur lors de la génération audio"); setIsPlaying(false); setIsLoading(false); }
  };

  useEffect(() => { return () => { if (audioRef.current) audioRef.current.pause(); }; }, []);

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(157,23,77,0.5)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)', border: '2px solid rgba(255,255,255,0.3)' }}>
                <Building2 size={30} className="text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 drop-shadow-lg tracking-wide">{slide.title}</h1>
              <p className="text-lg md:text-xl text-white/90 mb-4 max-w-xl leading-relaxed drop-shadow">{slide.subtitle}</p>
              <div className="flex gap-2 flex-wrap justify-center">
                {['5 chapitres', `${slides.length} slides`, 'Audio inclus'].map((tag, i) => (
                  <span key={i} className="px-3 py-1 rounded-full text-xs font-medium text-white" style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.25)' }}>{tag}</span>
                ))}
              </div>
              <p className="mt-4 text-xs text-white/60 max-w-lg">Certificat d'Aptitude aux Fonctions d'Encadrement et de Responsable d'Unité d'Intervention Sociale</p>
            </div>
          </div>
        );
      case 'sommaire':
        return (
          <div className="h-full flex flex-col relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(157,23,77,0.6)' }} />
            <div className="relative z-10 flex flex-col h-full p-5 overflow-auto">
              <h2 className="text-lg font-bold text-white text-center mb-3 drop-shadow">{slide.title}</h2>
              <div className="flex-1 flex flex-col justify-center max-w-2xl mx-auto w-full">
                <div className="space-y-1.5">
                  {slide.sections.map((sec, i) => (
                    <div key={i} onClick={() => goToSlide(sec.targetSlide)} className="flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all duration-200 hover:scale-[1.02]" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ background: theme.secondary, color: 'white' }}>{sec.num}</div>
                      <span className="text-white text-[13px] font-medium leading-snug">{sec.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'section':
        return (
          <div className="flex flex-col items-center justify-center h-full relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(157,23,77,0.55)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8">
              <Sparkles size={28} className="text-white/80 mb-3" />
              <h2 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">{slide.title}</h2>
              {slide.subtitle && <p className="text-base md:text-lg text-white/90 mt-2 max-w-lg drop-shadow">{slide.subtitle}</p>}
            </div>
          </div>
        );
      case 'content':
        return (
          <div className="p-5 h-full overflow-auto flex flex-col items-center justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl w-full">
              <h2 className="text-lg font-bold mb-3" style={{ color: theme.primary }}>{slide.title}</h2>
              {slide.intro && <p className="text-[13px] leading-relaxed mb-2" style={{ color: theme.text }}>{slide.intro}</p>}
              {slide.highlight && <div className="p-2.5 rounded-lg text-[13px] italic mb-2" style={{ background: theme.primary + '12', borderLeft: `3px solid ${theme.primary}`, color: theme.text }}>{slide.highlight}</div>}
              {slide.intro2 && <p className="text-[13px] leading-relaxed mb-2" style={{ color: theme.text }}>{slide.intro2}</p>}
              {slide.points && <ul className="space-y-1 mb-2">{slide.points.map((p, i) => (<li key={i} className="flex items-start gap-2 text-[13px]" style={{ color: theme.text }}><span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: theme.primary }} /><span>{p}</span></li>))}</ul>}
              {slide.intro3 && <p className="text-[13px] leading-relaxed mb-2" style={{ color: theme.text }}>{slide.intro3}</p>}
              {slide.points2 && <ul className="space-y-1 mb-2">{slide.points2.map((p, i) => (<li key={i} className="flex items-start gap-2 text-[13px]" style={{ color: theme.text }}><span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: theme.secondary }} /><span>{p}</span></li>))}</ul>}
            </div>
          </div>
        );
      case 'grid':
        return (
          <div className="p-5 h-full overflow-auto flex flex-col items-center justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl w-full">
              <h2 className="text-lg font-bold mb-3 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="grid grid-cols-2 gap-2">{slide.items.map((item, i) => (<div key={i} className="p-2.5 rounded-lg text-[12px] leading-snug" style={{ background: 'white', border: `1px solid ${theme.primary}22` }}><span className="font-bold mr-1" style={{ color: theme.primary }}>{item.num}.</span><span style={{ color: theme.text }}>{item.label}</span></div>))}</div>
            </div>
          </div>
        );
      case 'comparison':
        return (
          <div className="p-5 h-full overflow-auto flex flex-col items-center justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl w-full">
              <h2 className="text-lg font-bold mb-3 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="space-y-2 mb-3">{slide.wrong.map((item, i) => (<div key={i} className="grid grid-cols-2 gap-2"><div className="p-2.5 rounded-lg text-[13px] bg-red-50 border border-red-200"><span className="text-red-700">{item.label}</span></div><div className="p-2.5 rounded-lg text-[13px] bg-green-50 border border-green-200"><span className="text-green-700">{item.correct}</span></div></div>))}</div>
              {slide.highlight && <div className="p-3 rounded-lg text-[13px] italic text-center" style={{ background: theme.primary + '12', borderLeft: `3px solid ${theme.primary}` }}>{slide.highlight}</div>}
            </div>
          </div>
        );
      case 'congratulations':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(157,23,77,0.6)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-2xl">
              <div className="text-5xl mb-4">&#127942;</div>
              <h2 className="text-3xl font-bold text-white mb-4 drop-shadow-lg">{slide.title}</h2>
              <p className="text-white text-lg mb-3 leading-relaxed drop-shadow">{slide.message}</p>
              <div className="space-y-1 w-full mb-3">{slide.details.map((d, i) => (<div key={i} className="flex items-center gap-2 px-2 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}><Shield size={12} className="text-white flex-shrink-0" /><span className="text-white text-[13px]">{d}</span></div>))}</div>
              <div className="p-3 rounded-xl w-full" style={{ background: 'rgba(255,255,255,0.9)' }}><p className="text-sm font-semibold" style={{ color: theme.primary }}>Rendez-vous dans le Quiz BC3 pour valider vos acquis !</p></div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  const slide = slides[currentSlide];
  return (
    <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: theme.bg }} data-testid="caferuis-bc3-presentation">
      <div style={{ minHeight: '480px', height: '480px' }}>{renderSlide(slide)}</div>
      <div className="w-full h-2 bg-gray-200"><div className="h-full rounded-r-full transition-all duration-300" style={{ width: `${progressPercent}%`, background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})` }} /></div>
      <div className="flex items-center justify-between p-3 bg-white border-t" style={{ borderColor: theme.primary + '30' }}>
        <button onClick={prevSlide} disabled={currentSlide === 0} className="flex items-center gap-1 px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-40" style={{ borderColor: theme.primary, color: theme.text }} data-testid="bc3-prev-btn"><ChevronLeft size={16} /> Précédent</button>
        <div className="flex items-center gap-2">
          <button onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))} className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: theme.primary }} data-testid="bc3-audio-btn">{isLoading ? <Loader2 size={16} className="animate-spin" /> : isPlaying ? <VolumeX size={16} /> : <Volume2 size={16} />}</button>
          <span className="font-bold text-sm" style={{ color: theme.primary }}>{currentSlide + 1} / {slides.length}</span>
          <span className="text-xs text-gray-400">{progressPercent}%</span>
        </div>
        <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40" style={{ background: theme.primary }} data-testid="bc3-next-btn">Suivant <ChevronRight size={16} /></button>
      </div>
    </div>
  );
};

export default CAFBC3Presentation;
